import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { queryKeys } from '../config/queryClient';
import {
  addDraftWidget,
  areDashboardLayoutsEqual,
  cloneDashboardLayout,
  getDefaultDashboardLayout,
  MIN_VISIBLE_DASHBOARD_WIDGETS,
  moveDraftWidget,
  removeDraftWidget,
  setDraftColumnCount,
  setDraftWidgetWidthSpan,
  swapDraftWidgets,
} from '../config/dashboardWidgets';
import type {
  DashboardWidgetMoveDirection,
  DashboardWidgetMoveOptions,
} from '../config/dashboardWidgets';
import {
  DashboardColumnCount,
  DashboardLayout,
  DashboardLayoutResponse,
  DashboardWidgetId,
  DashboardWidthSpan,
  DEFAULT_DASHBOARD_LAYOUT,
} from '../config/dashboardWidgets/types';
import { getDashboardLayoutSaveErrorMessage } from '../utils/dashboardLayoutErrors';

export const useDashboardLayoutEditor = (savedLayout: DashboardLayout) => {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardLayout | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const displayLayout = isEditing && draft ? draft : savedLayout;
  const isDirty = Boolean(
    isEditing && draft && !areDashboardLayoutsEqual(draft, savedLayout)
  );
  const canReset = Boolean(
    isEditing && draft && !areDashboardLayoutsEqual(draft, DEFAULT_DASHBOARD_LAYOUT)
  );

  const saveMutation = useMutation({
    mutationFn: (layout: DashboardLayout): Promise<DashboardLayoutResponse> =>
      apiService.updateDashboardLayout(layout),
    retry: 0,
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.dashboard.layout(), data);
      setIsEditing(false);
      setDraft(null);
      setSaveError(null);
    },
  });

  const mutateLayout = saveMutation.mutateAsync;
  const isSaving = saveMutation.isPending;

  const startEditing = useCallback(() => {
    setDraft(cloneDashboardLayout(savedLayout));
    setIsEditing(true);
    setSaveError(null);
  }, [savedLayout]);

  const cancelEditing = useCallback(() => {
    setDraft(null);
    setIsEditing(false);
    setSaveError(null);
  }, []);

  const resetToDefault = useCallback(() => {
    setDraft(getDefaultDashboardLayout());
    setSaveError(null);
  }, []);

  const save = useCallback(async () => {
    if (!draft || draft.widgets.length < MIN_VISIBLE_DASHBOARD_WIDGETS) {
      return;
    }

    setSaveError(null);

    try {
      await mutateLayout(draft);
    } catch (error) {
      console.error('Error saving dashboard layout:', error);
      const message = getDashboardLayoutSaveErrorMessage(error);
      if (message) {
        setSaveError(message);
      }
    }
  }, [draft, mutateLayout]);

  const addWidget = useCallback((widgetId: DashboardWidgetId) => {
    setDraft((current) => (current ? addDraftWidget(current, widgetId) : current));
    setSaveError(null);
  }, []);

  const removeWidget = useCallback((widgetId: DashboardWidgetId) => {
    setDraft((current) => (current ? removeDraftWidget(current, widgetId) : current));
    setSaveError(null);
  }, []);

  const moveWidget = useCallback(
    (
      widgetId: DashboardWidgetId,
      direction: DashboardWidgetMoveDirection,
      options?: DashboardWidgetMoveOptions
    ) => {
      setDraft((current) =>
        current ? moveDraftWidget(current, widgetId, direction, options) : current
      );
      setSaveError(null);
    },
    []
  );

  const swapWidgets = useCallback((sourceId: DashboardWidgetId, targetId: DashboardWidgetId) => {
    setDraft((current) => (current ? swapDraftWidgets(current, sourceId, targetId) : current));
    setSaveError(null);
  }, []);

  const setWidgetWidthSpan = useCallback(
    (widgetId: DashboardWidgetId, widthSpan: DashboardWidthSpan) => {
      setDraft((current) =>
        current ? setDraftWidgetWidthSpan(current, widgetId, widthSpan) : current
      );
      setSaveError(null);
    },
    []
  );

  const setColumnCount = useCallback((columnCount: DashboardColumnCount) => {
    setDraft((current) => (current ? setDraftColumnCount(current, columnCount) : current));
    setSaveError(null);
  }, []);

  return useMemo(
    () => ({
      isEditing,
      displayLayout,
      isDirty,
      canReset,
      saveError,
      isSaving,
      startEditing,
      cancelEditing,
      resetToDefault,
      save,
      addWidget,
      removeWidget,
      moveWidget,
      swapWidgets,
      setWidgetWidthSpan,
      setColumnCount,
    }),
    [
      isEditing,
      displayLayout,
      isDirty,
      canReset,
      saveError,
      isSaving,
      startEditing,
      cancelEditing,
      resetToDefault,
      save,
      addWidget,
      removeWidget,
      moveWidget,
      swapWidgets,
      setWidgetWidthSpan,
      setColumnCount,
    ]
  );
};
